import { isJwtExpired, getJwtScopes } from './jwt'
import Cookie from 'js-cookie'
import jwtDecode from 'jwt-decode'
import zipCodeToTimezone from 'zipcode-to-timezone'
import {
  parsePhoneNumberWithError,
  validatePhoneNumberLength,
} from 'libphonenumber-js'
import { Roles, Permissions, Components } from '../../types/enums'
import { DEFAULT_COUNTRY, DEFAULT_TIMEZONE } from 'src/constants'

export interface UserPermissions {
  [Components.APPOINTMENTS]: Permissions
}

export interface DecodedToken {
  id: string
  system: string
  firstName: string
  lastName: string
  user: string
  email: string
  entityAuthorization?: {
    networkAdmin?: string
    consultNetworks?: string[]
    consultCustomer?: string
    consultCustomers?: string[]
    patient?: string
    doctor?: string
    employer?: string
    customer?: string
    roles: Roles[]
  }
}

export const getURLSearchParam = (searchKey: string) => {
  const searchParams = new URLSearchParams(window.location.search)
  return searchParams.get(searchKey)
}

const isSessionExpired = (token: string) => isJwtExpired(token)
export const isEMRTokenValid = (token: string) => {
  if (!token) return false

  return !isSessionExpired(token)
}

const getQueryStringToken = () => {
  return new URLSearchParams(window.location.search).get('auth') as string
}

const getCookieToken = () => {
  return Cookie.get('token') as string
}

const getDefaultRoleFromToken = (token: string = '') => {
  const roles = getJwtScopes(token || getAuthToken())
  return roles ? roles[0] : null
}

const getRoles = (token: string = '') => {
  return getJwtScopes(token || getAuthToken())
}

const getUserPermissions = (token: string = ''): UserPermissions | null => {
  const userRoles = getRoles(token)
  const hasValidWriteRole = [
    Roles.Medops,
    Roles.MedopsAdmin,
    Roles.Network,
  ]?.some((writeRole) => userRoles?.includes(writeRole))

  if (hasValidWriteRole) {
    return {
      [Components.APPOINTMENTS]: Permissions.WRITE,
    }
  }
  return {
    [Components.APPOINTMENTS]: Permissions.READ,
  }
}

const getEvaluatorId = (token: string = '') => {
  const { entityAuthorization }: DecodedToken = jwtDecode(
    token || getAuthToken()
  )
  return entityAuthorization?.doctor ?? null
}

export const ProviderRoles = () => {
  return [Roles.Doctor, Roles.TruepillRPh, Roles.TruepillRN]
}

export const isProvider = (roles: Roles | Roles[] | null | undefined) => {
  if (!roles) {
    return false
  } else if (Array.isArray(roles)) {
    return roles.filter((value) => ProviderRoles().includes(value)).length > 0
  } else {
    return ProviderRoles().includes(roles)
  }
}

export const MedOpsRoles = () => {
  return [Roles.Medops, Roles.MedopsAdmin]
}

export const isMedOps = (roles: Roles | Roles[] | null | undefined) => {
  if (!roles) {
    return false
  } else if (Array.isArray(roles)) {
    return roles.filter((value) => MedOpsRoles().includes(value)).length > 0
  } else {
    return MedOpsRoles().includes(roles)
  }
}

const headers = {
  Authorization: `Bearer ${getCookieToken()}`,
  'Content-Type': 'application/json',
}

const getAuthToken = () => getCookieToken()

const getTomorrowDate = () => {
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  return tomorrow
}

const generateObjectId = () => {
  const timestamp = ((new Date().getTime() / 1000) | 0).toString(16)
  return (
    timestamp +
    'xxxxxxxxxxxxxxxx'
      .replace(/[x]/g, () => {
        return ((Math.random() * 16) | 0).toString(16)
      })
      .toLowerCase()
  )
}

const toTitleCase = (str: string) => {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
  })
}

const getFormattedAgeFromDOB = (dateOfBirth: string): string => {
  if (!dateOfBirth) return ''

  let age: number
  let ageText: string = ''

  dateOfBirth = dateOfBirth.replace(/-/gi, '')
  const year = Number(dateOfBirth.substring(0, 4))
  const month = Number(dateOfBirth.substring(4, 6)) - 1
  const day = Number(dateOfBirth.substring(6, 8))
  const today = new Date()
  age = today.getFullYear() - year
  if (
    today.getMonth() < month ||
    (today.getMonth() === month && today.getDate() < day)
  ) {
    age--
  }
  if (age === 0) {
    /* Get months */
    age =
      Number(today.getMonth()) < month ||
      (Number(today.getMonth()) === month && Number(today.getFullYear()) > year)
        ? 12 - month + today.getMonth()
        : today.getMonth() - month
    /* Fix by days */
    age = today.getDate() < day ? age - 1 : age
    /* Add unit of measurement */
    ageText = `${age} ${age === 1 ? 'month' : 'months'}`
  } else {
    /* Add unit of measurement */
    ageText = `${age} ${age === 1 ? 'yr' : 'yrs'}`
  }

  return ageText
}

const formatPatientDobInput = (dobText: string): string => {
  if (!dobText) return ''

  let dob = dobText.replace(/\D/g, '')
  if (dob.length > 2 && dob[2]) {
    dob = `${dob.slice(0, 2)}/${dob.slice(2, dob.length)}`
  }

  if (dob.length > 5 && dob[5]) {
    dob = `${dob.slice(0, 5)}/${dob.slice(5, dob.length)}`
  }

  if (dob.length > 10) {
    dob = dob.slice(0, 1)
  }

  return dob
}

const formatPhoneNumberInput = (phoneNumberText: string): string => {
  if (!phoneNumberText) return ''

  let phoneNumber = phoneNumberText.replace(/\D/g, '')
  if (phoneNumber.length > 3 && phoneNumber[3] !== '-') {
    phoneNumber = `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(
      3,
      phoneNumber.length
    )}`
  }

  if (phoneNumber.length > 7 && phoneNumber[7] !== '-') {
    phoneNumber = `${phoneNumber.slice(0, 7)}-${phoneNumber.slice(
      7,
      phoneNumber.length
    )}`
  }

  if (phoneNumber.length > 12) {
    phoneNumber = phoneNumber.slice(0, 12)
  }

  return phoneNumber
}

const isValidPhoneNumber = (contactPhone: string): boolean => {
  if (!contactPhone) return false
  if (validatePhoneNumberLength(contactPhone, DEFAULT_COUNTRY)) return false
  const parsedValue = parsePhoneNumberWithError(contactPhone, DEFAULT_COUNTRY)
  return parsedValue.isValid()
}

const isValidEmail = (email: string): boolean => {
  const re = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/
  return re.test(String(email).toLowerCase())
}

const isValidNumber = (str?: string): boolean => {
  if (!str) {
    return false
  }
  return !isNaN(Number(str))
}

/**
 * handles users keyboard enter and space press action
 * @param e keyboard event object
 * @param cb callback function
 */
const handleKeyPressAccessibility = (
  e: React.KeyboardEvent<HTMLDivElement | HTMLButtonElement>,
  cb: (args?: any) => void,
  arg?: any
) => {
  if (
    e.key === 'Enter' ||
    e.keyCode === 13 ||
    e.key === 'Space' ||
    e.keyCode === 32
  ) {
    cb(arg)
  }
}

const handleEnterKeyPressAccessibility = (
  e: React.KeyboardEvent<HTMLDivElement | HTMLButtonElement>,
  cb: (args?: any) => void,
  arg?: any
) => {
  if (e.key === 'Enter' || e.keyCode === 13) {
    cb(arg)
  }
}

const getTimeOnPage = (startDate: Date): number => {
  const endDate = new Date()
  return (endDate.getTime() - startDate.getTime()) / 1000
}

const getTimezoneFromZipCode = (zip: string): string => {
  const zipCode = zip.substring(0, 5)
  return zipCodeToTimezone.lookup(zipCode) ?? DEFAULT_TIMEZONE
}

const delay = (ms?: number | undefined) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export {
  isSessionExpired,
  getQueryStringToken,
  getCookieToken,
  headers,
  getAuthToken,
  getDefaultRoleFromToken,
  getRoles,
  getUserPermissions,
  getEvaluatorId,
  getTomorrowDate,
  generateObjectId,
  toTitleCase,
  getFormattedAgeFromDOB,
  formatPhoneNumberInput,
  formatPatientDobInput,
  isValidPhoneNumber,
  isValidEmail,
  isValidNumber,
  handleKeyPressAccessibility,
  handleEnterKeyPressAccessibility,
  getTimeOnPage,
  getTimezoneFromZipCode,
  delay,
}
